import React from 'react';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  propTypes,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_OMISE_FEE,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Calculates the total price in sub units for multiple line items.
 */
const lineItemsTotal = (lineItems, marketplaceCurrency) => {
  const amount = lineItems.reduce((total, item) => {
    return total.plus(item.lineTotal.amount);
  }, new Decimal(0));
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : marketplaceCurrency;
  return new Money(amount, currency);
};

/**
 * Checks if line item represents commission
 */
const isCommission = lineItem => {
  return (
    lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION ||
    lineItem.code === LINE_ITEM_OMISE_FEE
  );
};

/**
 * Checks if line item is an overtime line item
 * (codes are like "line-item/overtime 5:00 pm 6:00 pm")
 */
const isOvertime = lineItem => {
  return typeof lineItem.code === 'string' && lineItem.code.startsWith('line-item/overtime');
};

/**
 * Returns non-commission, non-reversal, non-overtime line items
 * (used for the "Subtotal" row)
 */
const nonCommissionNonReversalLineItems = lineItems => {
  return lineItems.filter(item => !isCommission(item) && !item.reversal && !isOvertime(item));
};

/**
 * Check if there is a commission line-item for the given user role.
 */
const hasCommission = (lineItems, userRole) => {
  let commissionLineItem = null;

  if (userRole === 'customer') {
    // Check for either LINE_ITEM_CUSTOMER_COMMISSION or LINE_ITEM_OMISE_FEE for customers
    commissionLineItem = lineItems.find(
      item => item.code === LINE_ITEM_CUSTOMER_COMMISSION || item.code === LINE_ITEM_OMISE_FEE
    );
  } else if (userRole === 'provider') {
    // Check for LINE_ITEM_PROVIDER_COMMISSION for providers
    commissionLineItem = lineItems.find(item => item.code === LINE_ITEM_PROVIDER_COMMISSION);
  }

  // Return true if a commission line item is found, false otherwise
  return !!commissionLineItem;
};

/**
 * A component that renders the subtotal as a line item.
 */
const LineItemSubTotalMaybe = props => {
  const { lineItems, code, userRole, intl, marketplaceCurrency, isDeclinedBookings } = props;

  const refund = lineItems.find(item => item.code === code && item.reversal);

  // Show subtotal only if commission line-item is applicable to user or refund is issued.
  const showSubTotal = hasCommission(lineItems, userRole) || refund;

  // all non-reversal, non-commission, non-overtime line items
  const subTotalLineItems = nonCommissionNonReversalLineItems(lineItems);
  // line totals of those line items combined
  const subTotal = lineItemsTotal(subTotalLineItems, marketplaceCurrency);

  const formattedSubTotal = subTotalLineItems.length > 0 ? formatMoney(intl, subTotal) : null;

  return formattedSubTotal && showSubTotal ? (
    <>
      {isDeclinedBookings ? <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.bookingDeclined" />
        </span>
      </div> :

        <div className={css.subTotalLineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.price" />
          </span>
          <span className={css.itemValue}>{formattedSubTotal}</span>
        </div>}
    </>
  ) : null;
};

export default LineItemSubTotalMaybe;
